import React, { useEffect, useRef, useState, useContext, useCallback } from "react";
import { SettingsContext } from "@/SettingsContext";
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { usePointPreview } from '/src/PointPreviewContext';
import { point, lineString, nearestPointOnLine, distance } from '@turf/turf';
import { useNavigation } from '/src/NavigationContext';
import { useLocation } from '/src/LocationContext';
import axiosInstance from "../../axiosInstance";

// Define marker icons
const markerIcons = {
  locked: '/static/markers/locked.svg',
  visited: '/static/markers/visited.svg',
  answer_correct: '/static/markers/answer_correct.svg',
  answer_wrong: '/static/markers/answer_wrong.svg',
  default: '/static/markers/default.svg'
};

export default function MapLibreMap({ currentLocation, points, onItemClick }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const userMarker = useRef(null);
  const pointMarkers = useRef([]);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [isValidLocation, setIsValidLocation] = useState(false);
  const { 
    activeRoute, 
    setActiveRoute, 
    setNavigationEnabled, 
    nextInstruction, 
    setNextInstruction, 
    routeInstructions, 
    setRouteInstructions, 
    distanceToNextTurn, 
    setDistanceToNextTurn,
    isOffRoute, 
    setIsOffRoute,
    stopNavigation,
    setRouteFailure,
    clearRouteError,
    calculatingRoute,
    setCalculatingRoute
  } = useNavigation();
  //const [routeInstructions, setRouteInstructions] = useState([]);
  //const [nextInstruction, setNextInstruction] = useState(null);
  //const [distanceToNextTurn, setDistanceToNextTurn] = useState(null);
  //const [isOffRoute, setIsOffRoute] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  
  const { tourSettings } = useContext(SettingsContext);
  const { openPreview } = usePointPreview();
  const { getGPSPosition } = useLocation();
  
  // Add a ref to track the last time we recalculated the route
  const lastRouteCalculationRef = useRef(Date.now());
  
  // Add a ref to store the previous location for smoother transitions
  const previousLocationRef = useRef(null);
  
  // Add ref to track the last time we updated the map view
  const lastViewUpdateRef = useRef(Date.now());
  
  // Add these state variables and refs
  const [smoothedBearing, setSmoothedBearing] = useState(0);
  const lastValidBearingRef = useRef(null);
  const bearingBufferRef = useRef([]);
  const lastAnimationTimeRef = useRef(0);
  const MIN_ANIMATION_INTERVAL = 300; // Minimum time between animations in ms
  
  // Check if current location is valid
  useEffect(() => {
    console.log("currentLocation", currentLocation);
    if (currentLocation && 
        currentLocation.lat !== undefined && 
        currentLocation.lng !== undefined && 
        (currentLocation.lat !== 0 || currentLocation.lng !== 0)) {
      setIsValidLocation(true);
    } else {
      setIsValidLocation(false);
    }
  }, [currentLocation]);

  // Initialize map
  useEffect(() => {
    console.log("points", points);

    if (map.current) return; // Map already initialized

    if (!points || Object.keys(points).length === 0) {
      console.log("no points found");
      return;
    }

    console.log("Initializing map");

    // get the location of the first point and use that as the center of the map
    const firstPoint = Object.values(points)[0];

    console.log("firstPoint", firstPoint?.data?.gps_location);

    if (!firstPoint?.data?.gps_location) {
      console.log("no GPS position found for the first point");
      return;
    }

    const center = getGPSPosition(firstPoint.data.gps_location);

    console.log("center", center);
    
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      // Use OpenStreetMap vector tiles
      style: {
        version: 8,
        sources: {
          'osm': {
            type: 'raster',
            tiles: [
              'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
            ],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }
        },
        layers: [
          {
            id: 'osm',
            type: 'raster',
            source: 'osm',
            minzoom: 0,
            maxzoom: 19
          }
        ]
      },
      center: center, // Use the first point as the center
      zoom: 13,
      attributionControl: false,
      bearing: 0,
      pitch: 0
    });

    // Wait for map style to load before adding sources and layers
    map.current.on('styledata', function() {
      if (map.current.isStyleLoaded()) {
        console.log("Map style loaded");

        // Only set isMapInitialized and add sources/layers if style is fully loaded
        if (!isMapInitialized) {
          setIsMapInitialized(true);

          // Add all sources and layers
          addMapSources();

          // Fit bounds to points if available
          fitMapToPoints();
        }
      }
    });

    // Also listen for the load event as a fallback
    map.current.on('load', () => {
      console.log("Map loaded");

      // Check if style is loaded before proceeding
      if (map.current.isStyleLoaded() && !isMapInitialized) {
        setIsMapInitialized(true);

        // Add all sources and layers
        addMapSources();

        // Fit bounds to points if available
        fitMapToPoints();
      }
    });

    // Cleanup on unmount
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [points]);

  // Modify the addMapSources function to include error handling
  const addMapSources = useCallback(() => {
    if (!map.current) return;

    console.log("Adding map sources and layers");

    try {
      // Check if style is loaded before adding sources
      if (!map.current.isStyleLoaded()) {
        console.warn("Map style not fully loaded yet, delaying source addition");
        // Try again after a short delay
        setTimeout(addMapSources, 100);
        return;
      }


      // Add empty sources and layers for route
      if (!map.current.getSource('route')) {
        map.current.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: []
            }
          }
        });

        map.current.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
            'visibility': 'none'
          },
          paint: {
            'line-color': '#4285F4',
            'line-width': 5,
            'line-opacity': 0.7
          }
        });
      }

      // Add layer for turn points
      if (!map.current.getSource('turns')) {
        map.current.addSource('turns', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });

        map.current.addLayer({
          id: 'turns',
          type: 'circle',
          source: 'turns',
          layout: {
            'visibility': 'none'
          },
          paint: {
            'circle-radius': 8,
            'circle-color': '#FF8C00',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#FFFFFF'
          }
        });
      }

      // Add layer for nearest point on route
      if (!map.current.getSource('nearest-point')) {
        map.current.addSource('nearest-point', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Point',
              coordinates: [0, 0]
            }
          }
        });

        map.current.addLayer({
          id: 'nearest-point',
          type: 'circle',
          source: 'nearest-point',
          layout: {
            'visibility': 'none'
          },
          paint: {
            'circle-radius': 6,
            'circle-color': '#00FF00',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#FFFFFF'
          }
        });
      }

      // Add active-turn source and layer
      console.log("Adding active-turn source and layer");
      if (!map.current.getSource('active-turn')) {
        map.current.addSource('active-turn', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Point',
              coordinates: [0, 0]
            }
          }
        });

        map.current.addLayer({
          id: 'active-turn',
          type: 'circle',
          source: 'active-turn',
          paint: {
            'circle-radius': 12,
            'circle-color': '#ff9500',
            'circle-opacity': 0.8,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          },
          layout: {
            'visibility': 'none'
          }
        });
      }


      console.log("All map sources and layers added successfully");
} catch (error) {
  console.error("Error adding map sources and layers:", error);
  // Try again after a short delay
  setTimeout(addMapSources, 500);
}
}, [tourSettings]);


  // Make sure fitMapToPoints also checks if style is loaded
  const fitMapToPoints = useCallback(() => {
    if (!map.current || !points || Object.keys(points).length === 0) return;

    // Check if style is loaded before proceeding
    if (!map.current.isStyleLoaded()) {
      console.warn("Map style not fully loaded yet, delaying fitMapToPoints");
      // Try again after a short delay
      setTimeout(fitMapToPoints, 100);
      return;
    }

    console.log("Fitting map to points and current location");

    try {
      // Create bounds object
      const bounds = new maplibregl.LngLatBounds();
      let hasValidBounds = false;

      // Add all points to bounds
      Object.values(points).forEach(point => {
        if (point?.data?.gps_location) {
          const gpsPosition = getGPSPosition(point.data.gps_location);
          if (!isNaN(gpsPosition.lat) && !isNaN(gpsPosition.lng)) {
            bounds.extend(gpsPosition);
            hasValidBounds = true;
          }
        }
      });

      // Add current location to bounds if valid
      if (isValidLocation) {
        console.log("adding current location to bounds");
        bounds.extend([currentLocation.lng, currentLocation.lat]);
        hasValidBounds = true;
      }

      // If we have valid bounds, fit the map to them
      if (hasValidBounds) {
        map.current.fitBounds(bounds, {
          padding: { top: 120, bottom: 150, left: 50, right: 50 },
          maxZoom: 16
        });
      }


    } catch (error) {
      console.error("Error fitting map to points or current location:", error);
    }
  }, [points, isValidLocation, currentLocation]);
  
  // Update user location marker and accuracy circle
  useEffect(() => {
    if (!map.current || !isMapInitialized) return;
    
    // If location is valid, update user marker and accuracy circle
    if (isValidLocation) {
      // Store the previous valid location
      if (!previousLocationRef.current) {
        previousLocationRef.current = { ...currentLocation };
      }
      
      // Create or update user location marker
      if (!userMarker.current) {
        // Create a DOM element for the marker
        const el = document.createElement('div');
        el.className = 'user-location-marker';
        el.style.width = '20px';
        el.style.height = '20px';
        el.style.borderRadius = '50%';
        el.style.backgroundColor = '#4285F4';
        el.style.border = '2px solid white';
        el.style.boxShadow = '0 0 5px rgba(0,0,0,0.3)';
        
        // Create the marker
        userMarker.current = new maplibregl.Marker({
          element: el,
          rotationAlignment: 'map'
        })
          .setLngLat([currentLocation.lng, currentLocation.lat])
          .addTo(map.current);
      } else {
        // Update existing marker position with animation
        userMarker.current.setLngLat([currentLocation.lng, currentLocation.lat]);
      }

      // // update accuracy circle based on current location
      // console.log("Initializing accuracy circle with current location");
      // if (map.current.getSource('accuracy-circle')) {
      // map.current.getSource('accuracy-circle').setData({
      //   type: 'Feature',
      //   properties: {
      //           radius: tourSettings.navigation.google_maps.gps_radius || 20  // TODO depending on the zoom level
      //         },
      //         geometry: {
      //           type: 'Point',
      //           coordinates: [currentLocation.lng, currentLocation.lat]
      //         }
      //       });
      // }
      
      // Rotate marker based on heading if available
      // if (currentLocation.heading !== undefined && userMarker.current) {
      //   const markerEl = userMarker.current.getElement();
      //   markerEl.style.transform = `${markerEl.style.transform} rotate(${currentLocation.heading}deg)`;
      // }

      // Update previous location ref
      previousLocationRef.current = { ...currentLocation };
      
      // Rotate map based on heading if available and user is moving
      // if (currentLocation.heading !== undefined && currentLocation.speed && currentLocation.speed > 0.5) {
      //   map.current.setBearing(currentLocation.heading);
      // }
    }
  }, [currentLocation, isValidLocation, isMapInitialized, tourSettings]);
  
  // // Initialize accuracy circle source and layer
  // useEffect(() => {
  //   if (!map.current || !isMapInitialized) return;
  //
  //   console.log("Adding accuracy circle source and layer");
  //   console.log("tourSettings", tourSettings);
  //   console.log("tourSettings.gps_radius", tourSettings.navigation.google_maps.gps_radius);
  //
  //   console.log("currentLocation", currentLocation);
  //   console.log("isValidLocation", isValidLocation);
  //
  //
  //
  //   // Add accuracy circle source and layer if they don't exist
  //   if (!map.current.getSource('accuracy-circle')) {
  //     map.current.addSource('accuracy-circle', {
  //       type: 'geojson',
  //       data: {
  //         type: 'Feature',
  //         properties: {
  //           radius: tourSettings.navigation.google_maps.gps_radius || 10
  //         },
  //         geometry: {
  //           type: 'Point',
  //           coordinates: [currentLocation.lng, currentLocation.lat]
  //         }
  //       }
  //     });
  //
  //     map.current.addLayer({
  //       id: 'accuracy-circle',
  //       type: 'circle',
  //       source: 'accuracy-circle',
  //       paint: {
  //         'circle-radius': ['get', 'radius'],
  //         'circle-color': '#4285F4',
  //         'circle-opacity': 0.2,
  //         'circle-stroke-width': 1,
  //         'circle-stroke-color': '#4285F4',
  //         'circle-stroke-opacity': 0.7
  //       }
  //     });
  //   }
  //
  //
  // }, [isMapInitialized, isValidLocation, currentLocation]);
  
  // Add point markers
  useEffect(() => {
    if (!map.current || !isMapInitialized) return;
    
    // Remove existing markers
    pointMarkers.current.forEach(marker => marker.remove());
    pointMarkers.current = [];
    
    // Add new markers for each point
    Object.entries(points).forEach(([pointId, point]) => {
      if (!point?.data?.gps_location) return;
      
      const [lat, lng] = point.data.gps_location.split(',').map(parseFloat);
      if (isNaN(lat) || isNaN(lng)) return;
      
      // Create marker element
      const el = document.createElement('div');
      el.className = 'point-marker';
      
      // Use custom icon if available
      if (point.data?.icon?.preview_url) {
        el.style.backgroundImage = `url(${point.data.icon.preview_url})`;
        el.style.width = '36px';
        el.style.height = '36px';
        el.style.backgroundSize = 'contain';
      } else {
        // Use default marker based on state
        const markerIcon = markerIcons[point.state] || markerIcons.default;
        el.style.backgroundImage = `url(${markerIcon})`;
        el.style.width = '36px';
        el.style.height = '36px';
        el.style.backgroundSize = 'contain';
        
        // Add point number
        if (point.data.order) {
          const numberEl = document.createElement('div');
          numberEl.className = 'point-number';
          numberEl.style.position = 'absolute';
          numberEl.style.top = '4px';
          numberEl.style.left = '50%';
          numberEl.style.transform = 'translateX(-50%)';
          numberEl.style.backgroundColor = 'white';
          numberEl.style.color = 'black';
          numberEl.style.borderRadius = '50%';
          numberEl.style.width = '20px';
          numberEl.style.height = '20px';
          numberEl.style.display = 'flex';
          numberEl.style.alignItems = 'center';
          numberEl.style.justifyContent = 'center';
          numberEl.style.fontSize = '14px';
          numberEl.style.fontWeight = 'bold';
          numberEl.textContent = point.data.order;
          el.appendChild(numberEl);
        }
      }
      
      // Create and add marker
      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'bottom'
      })
        .setLngLat([lng, lat])
        .addTo(map.current);
      
      // Add click handler
      marker.getElement().addEventListener(
        'click', 
        () => {
          if (!point.locked) {
            console.log("handle onItemClick", point.data.id, point);
            
            // Set selected point
            setSelectedPoint(point);
            
            // Show preview with navigation option
            openPreview(
              point, 
              (selectedPoint) => {
                // This will run when the user clicks "Navigate" in the preview
                if (onItemClick) {
                  console.log("action callback, open the overlay");
                  // now we can open the overlay
                  onItemClick(selectedPoint.data, false);
                }
                // we can have multiple events for the same point, so we need to check if navigation button was clicked
              }, 
              true, 
              () => {
                console.log("navigation callback");
                // Calculate route to this point
                if (isValidLocation) {
                  console.log("calculate route");
                  calculateRoute(
                      [currentLocation.lng, currentLocation.lat],
                      [lng, lat],
                      point.data
                  );
                }
              });
            }
        });
      
      // Store marker reference for cleanup
      pointMarkers.current.push(marker);
    });
  }, [points, isMapInitialized, onItemClick, isValidLocation, currentLocation, openPreview]);
  
  // Calculate direct route (as-the-crow-flies)
  const calculateDirectRoute = useCallback((start, end, pointData) => {
    if (!map.current) return;
    
    console.log("Using direct route (as-the-crow-flies) since no proper route is available");
    
    // Create a simple LineString with just start and end points
    const directGeometry = {
      type: 'LineString',
      coordinates: [start, end]
    };
    
    // Store route data
    setActiveRoute({
      geometry: directGeometry,
      distance: distance(point(start), point(end), { units: 'meters' }),
      duration: null, // We don't know the duration for a direct route
      legs: [],
      destination: {
        coordinates: end,
        pointData: pointData
      }
    });
    
    // Set a single instruction
    setRouteInstructions([{
      text: `Loop rechstreeks naar het punt.`,
      distance: distance(point(start), point(end), { units: 'meters' }),
      type: "direct",
      modifier: "straight",
      location: end
    }]);
    
    // Update route on map
    if (map.current.getSource('route')) {
      map.current.getSource('route').setData({
        type: 'Feature',
        properties: {},
        geometry: directGeometry
      });
      map.current.setLayoutProperty('route', 'visibility', 'visible');
    }
    
    // Hide turn points
    if (map.current.getSource('turns')) {
      map.current.getSource('turns').setData({
        type: 'FeatureCollection',
        features: []
      });
      map.current.setLayoutProperty('turns', 'visibility', 'none');
    }
    
    // Fit map to show route
    const routeBounds = new maplibregl.LngLatBounds()
      .extend(start)
      .extend(end);
    
    map.current.fitBounds(routeBounds, {
      padding: { top: 50, bottom: 150, left: 50, right: 50 },
      maxZoom: 16
    });
    
    // Initialize navigation
    updateNavigation();
  }, [updateNavigation]);
  
  // Calculate route between two points
  const calculateRoute = useCallback((start, end, pointData) => {
    if (!map.current || !isMapInitialized) return;
    
    // Prevent multiple simultaneous route calculations
    if (calculatingRoute) {
      console.log('Route calculation already in progress, skipping');
      return;
    }
    

    const now = Date.now();

    // Throttle route calculations to at most once every 10 seconds

    // const timeSinceLastCalculation = now - lastRouteCalculationRef.current;
    // if (timeSinceLastCalculation < 10000) {
    //   console.log(`Skipping route calculation, last one was ${timeSinceLastCalculation}ms ago`);
    //   return;
    // }
    
    console.log(`Calculating route from [${start}] to [${end}]`);
    setCalculatingRoute(true);
    
    // Fetch route from OSRM - explicitly use walking profile
    
    axiosInstance.get(`${process.env.CLIENT_API_URL}/client/routing/v1/foot/${start[0]},${start[1]};${end[0]},${end[1]}?overview=full&geometries=geojson&steps=true`)
     // .then(response => response.json())
      .then(response => {
        const data = response.data;
        setCalculatingRoute(false);
        lastRouteCalculationRef.current = now;
        
        // Check if route calculation was successful
        if (data.code !== 'Ok' || !data.routes || data.routes.length === 0) {
          console.error('Error fetching route:', data.message || 'No routes found');
          setRouteFailure(true);
          return;
        }
        
        // Process route data
        processRouteData(data, start, end, pointData);
      })
      .catch(error => {
        setCalculatingRoute(false);
        console.error('Error fetching route:', error);
        
        setRouteFailure(true);
        // Fall back to direct route after a network error
        //calculateDirectRoute(start, end, pointData);
      });
  }, [isMapInitialized, updateNavigation, calculateDirectRoute]);
  
  // Helper function to process route data
  const processRouteData = useCallback((data, start, end, pointData) => {
    const route = data.routes[0];
    
    // Check if the route is unreasonably long compared to direct distance
    // const directDist = distance(point(start), point(end), { units: 'meters' });
    // if (route.distance > directDist * 3) {
    //   console.log(`Route is ${route.distance}m but direct distance is only ${directDist}m. Using direct route.`);
    //   //routeFailuresRef.current += 1;
    //   calculateDirectRoute(start, end, pointData);
    //   return;
    // }
    
    // Reset failure counter on success
    clearRouteError();
    setCalculatingRoute(false);
    const routeGeometry = route.geometry;
    
    // Store route data
    setActiveRoute({
      geometry: routeGeometry,
      distance: route.distance,
      duration: route.duration,
      legs: route.legs,
      destination: {
        coordinates: end,
        pointData: pointData
      }
    });
    
    // Extract turn instructions
    const instructions = [];
    route.legs.forEach(leg => {
      leg.steps.forEach(step => {
        instructions.push({
          text: step.maneuver.instruction || step.name,
          distance: step.distance,
          duration: step.duration,
          type: step.maneuver.type,
          modifier: step.maneuver.modifier,
          location: step.maneuver.location,
          bearing_before: step.maneuver.bearing_before,
          bearing_after: step.maneuver.bearing_after
        });
      });
    });
    console.log("setRouteInstructions", instructions);

    setRouteInstructions(instructions);
    
    // Update route on map
    if (map.current.getSource('route')) {
      map.current.getSource('route').setData({
        type: 'Feature',
        properties: {},
        geometry: routeGeometry
      });
      map.current.setLayoutProperty('route', 'visibility', 'visible');
    }
    
    // Add turn points to map
    const turnFeatures = instructions.map(instruction => ({
      type: 'Feature',
      properties: {
        description: instruction.text,
        type: instruction.type
      },
      geometry: {
        type: 'Point',
        coordinates: instruction.location
      }
    }));
    
    if (map.current.getSource('turns')) {
      map.current.getSource('turns').setData({
        type: 'FeatureCollection',
        features: turnFeatures
      });
      map.current.setLayoutProperty('turns', 'visibility', 'visible');
    }
    
    // Fit map to show route
    const routeBounds = new maplibregl.LngLatBounds();
    routeGeometry.coordinates.forEach(coord => {
      routeBounds.extend(coord);
    });
    
    map.current.fitBounds(routeBounds, {
      padding: { top: 50, bottom: 150, left: 50, right: 50 },
      maxZoom: 16
    });
    
    // Initialize navigation
    updateNavigation();
  }, [calculateDirectRoute, updateNavigation]);
  
  // Update navigation based on current location and route
  const updateNavigation = useCallback(() => {
    console.log("updateNavigation", activeRoute, isValidLocation, map.current);
    if (!activeRoute || !isValidLocation || !map.current) return;
    
    try {
      // Create points for current location and route
      const userPoint = point([currentLocation.lng, currentLocation.lat]);
      const routeLine = lineString(activeRoute.geometry.coordinates);
      
      // Find nearest point on route
      const nearest = nearestPointOnLine(routeLine, userPoint);
      const nearestCoord = nearest.geometry.coordinates;
      
      // Update nearest point marker
      if (map.current.getSource('nearest-point')) {
        map.current.getSource('nearest-point').setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: nearestCoord
          }
        });
        map.current.setLayoutProperty('nearest-point', 'visibility', 'visible');
      }
      
      // Calculate distance to nearest point (in meters)
      const distanceToRoute = nearest.properties.dist * 1000;
      
      // Check if user is off route (more than 20 meters away)
      const offRouteThreshold = 20;
      setIsOffRoute(distanceToRoute > offRouteThreshold);
      
      if (distanceToRoute > offRouteThreshold) {
        console.log(`Off route: ${distanceToRoute.toFixed(1)}m from route`);
        
        // Recalculate route if significantly off route (more than 50 meters)
        // AND at least 10 seconds have passed since the last calculation
        // AND we're not already calculating a route
        if (distanceToRoute > 50 && 
            activeRoute.destination && 
            !calculatingRoute &&
            (Date.now() - lastRouteCalculationRef.current) > 10000) {
          
          console.log("Recalculating route due to significant deviation");
          calculateRoute(
            [currentLocation.lng, currentLocation.lat],
            activeRoute.destination.coordinates,
            activeRoute.destination.pointData
          );
        }
      }
      
      // Calculate direct distance to destination
      let directDistance = Infinity;
      if (activeRoute && activeRoute.destination) {
        const destPoint = point(activeRoute.destination.coordinates);
        directDistance = distance(userPoint, destPoint, { units: 'meters' });
      }
      
      // Find next instruction
      const routeProgress = nearest.properties.location; // Position along the route (0-1)
      const routeLength = activeRoute.distance; // Total route length in meters
      const distanceTraveled = routeProgress * routeLength;

      console.log(`Route progress: ${(routeProgress * 100).toFixed(1)}%, traveled: ${distanceTraveled.toFixed(1)}m of ${routeLength.toFixed(1)}m`);
   
      // Check if we've arrived at destination (within 10 meters)
      const hasArrived = directDistance <= 10;

      if (hasArrived) {
        console.log("Arrived at destination! Distance: " + directDistance.toFixed(1) + "m");
      
        // Show arrival message
        setNextInstruction({
          text: "Je bent aangekomen bij je bestemming",
          distance: 0,
          type: "arrive"
        });
        setDistanceToNextTurn(0);
        
        // Stop navigation automatically after a short delay
        setTimeout(() => {
          stopNavigation();
        }, 3000);
      } else {
      // Find all upcoming instructions with their actual distances
      const upcomingInstructions = [];
      
      // Process each instruction
      if (routeInstructions && routeInstructions.length > 0) {
        for (let i = 0; i < routeInstructions.length; i++) {
          const instruction = routeInstructions[i];
          
          // Skip instructions without location
          if (!instruction.location) continue;
          
          // Calculate actual distance from user to this instruction point
          const instructionPoint = point(instruction.location);
          const directDistanceToInstruction = distance(userPoint, instructionPoint, { units: 'meters' });
          
          // Calculate distance along route to this instruction
          // First find where this instruction is on the route
          const instructionOnRoute = nearestPointOnLine(routeLine, instructionPoint);
          const instructionRouteProgress = instructionOnRoute.properties.location;
          
          // Only include instructions that are ahead of us on the route
          if (instructionRouteProgress > routeProgress) {
            // Calculate distance along the route (not direct)
            const distanceAlongRoute = (instructionRouteProgress - routeProgress) * routeLength;
            
            upcomingInstructions.push({
              index: i,
              instruction: instruction,
              directDistance: directDistanceToInstruction,
              routeDistance: distanceAlongRoute,
              routeProgress: instructionRouteProgress
            });
          }
        }
      }
      
        console.log(`Found ${upcomingInstructions.length} upcoming instructions`);

        // Sort upcoming instructions by distance
        upcomingInstructions.sort((a, b) => a.distanceFromUser - b.distanceFromUser);
      
        // If we have upcoming instructions
        if (upcomingInstructions.length > 0) {
          // Get the next instruction (closest one along the route)
          const nextInstructionData = upcomingInstructions[0];
          const instruction = nextInstructionData.instruction;
          const distanceToTurn = Math.max(1, Math.round(nextInstructionData.routeDistance)); // Ensure at least 1m
        
          console.log(`Next instruction: "${instruction.text}" in ${distanceToTurn}m (direct: ${Math.round(nextInstructionData.directDistance)}m)`);
       
          // Check if we're very close to this instruction (within 10 meters)
          const isVeryCloseToTurn = distanceToTurn <= 10;
          
          // If we're very close to a turn, also show the following instruction if available
          if (isVeryCloseToTurn && upcomingInstructions.length > 1) {
            const nextNextInstruction = upcomingInstructions[1].instruction;
            const combinedText = `${instruction.text}, daarna ${nextNextInstruction.text}`;
            
            setNextInstruction({
              ...instruction,
              text: combinedText
            });
            console.log(`At turn point: ${combinedText}`);
          } else {
            setNextInstruction(instruction);
          }
      
          // Force a state update by creating a new value
          const newDistanceToNextTurn = distanceToTurn;
          console.log(`Setting distance to next turn: ${newDistanceToNextTurn}m (was: ${distanceToNextTurn}m)`);
          setDistanceToNextTurn(newDistanceToNextTurn);
          
          // If we're very close to a turn, highlight it on the map
          if (isVeryCloseToTurn && instruction.location) {
            console.log(`Very close to turn, highlighting at ${JSON.stringify(instruction.location)}`);
          
            // Highlight the upcoming turn point on the map
            if (map.current.getSource('active-turn')) {
              try {
                map.current.getSource('active-turn').setData({
                  type: 'Feature',
                  properties: {
                    description: instruction.text
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: instruction.location
                  }
                });
                map.current.setLayoutProperty('active-turn', 'visibility', 'visible');
                console.log("Active turn marker updated and made visible");
              } catch (error) {
                console.error("Error updating active-turn source:", error);
              }
            } else {
              console.warn("active-turn source not found");
            }
          } else {
            // Hide the active turn highlight when not close to a turn
            if (map.current.getSource('active-turn')) {
              map.current.setLayoutProperty('active-turn', 'visibility', 'none');
            }
          }
        } else {
          // No more instructions, but we haven't arrived yet
          // This can happen if we're between the last instruction and the destination
          console.log(`No upcoming instructions, continuing to destination (${directDistance.toFixed(1)}m away)`);
          setNextInstruction({
            text: "Ga door naar je bestemming",
            distance: directDistance,
            type: "continue"
          });
          setDistanceToNextTurn(Math.round(directDistance));
        }
      }

    } catch (error) {
      console.error('Error updating navigation:', error);
    }
  }, [activeRoute, currentLocation, isValidLocation, routeInstructions, calculateRoute, setNextInstruction, setDistanceToNextTurn, setIsOffRoute, stopNavigation]);
  
  // Make sure the navigation updates frequently when location changes
  useEffect(() => {
    if (activeRoute && isValidLocation) {
      console.log("Location changed, updating navigation");
      updateNavigation();
    }
  }, [currentLocation, activeRoute, isValidLocation, updateNavigation]);
  
  // Update map view based on user location
  useEffect(() => {
    if (!map.current || !isMapInitialized || !isValidLocation) return;



      // Check if heading is valid
      const isValidHeading = currentLocation.heading !== undefined && 
        currentLocation.heading !== null && 
        !isNaN(currentLocation.heading);

      // Check if speed is valid and above threshold
      const isMovingFastEnough = currentLocation.speed && 
          currentLocation.speed >= 0.9;

      // Check if accuracy is good enough
      const hasGoodAccuracy = currentLocation.accuracy && 
        currentLocation.accuracy < 20;
  
  // Update bearing smoothly if we have valid data and are moving fast enough
  if (isValidHeading && isMovingFastEnough && hasGoodAccuracy) {
    console.log(`Updating bearing: ${currentLocation.heading.toFixed(1)}° at speed ${currentLocation.speed.toFixed(1)} m/s`);
    
    // Add to buffer for smoothing (keep last 5 valid bearings)
    bearingBufferRef.current.push(currentLocation.heading);
    if (bearingBufferRef.current.length > 5) {
      bearingBufferRef.current.shift();
    }
    
    // Calculate average bearing from buffer
    const sum = bearingBufferRef.current.reduce((acc, val) => acc + val, 0);
    const avg = sum / bearingBufferRef.current.length;
    
    // Store as last valid bearing
    lastValidBearingRef.current = avg;
    setSmoothedBearing(avg);
  }
  
    // Determine bearing to use for map
    let bearing = 0;
    let zoomLevel = 16;
    
    if (activeRoute) {
      zoomLevel = 17;
      console.log("we have an active route, so we use the last valid bearing or current heading if moving");
      // When navigating, always use the last valid bearing or current heading if moving
      if (isValidHeading && isMovingFastEnough && hasGoodAccuracy) {
        bearing = smoothedBearing;
        
      } else if (lastValidBearingRef.current !== null) {
        // When stopped or slow, keep using the last valid bearing
        bearing = lastValidBearingRef.current;
        console.log(`Using last valid bearing: ${bearing.toFixed(1)}° (stopped or slow)`);
      }
    } else {
      console.log("we don't have an active route, so we use the last valid bearing or current heading if moving");
      // When not navigating, only use bearing when moving
      if (isValidHeading && isMovingFastEnough && hasGoodAccuracy) {
        console.log("using smoothed bearing when not navigating but moving fast enough with good accuracy and heading");
        bearing = smoothedBearing;
        zoomLevel = 17;
      } else if (lastValidBearingRef.current !== null) {
        // When stopped or slow, keep using the last valid bearing
        bearing = lastValidBearingRef.current;
        zoomLevel = 17;
        console.log(`Using last valid bearing: ${bearing.toFixed(1)}° (stopped or slow)`);
      
      } else {  
        console.log("resetting to north when not navigating and not moving");
        // Reset to north when not navigating and not moving
        bearing = 0;
        zoomLevel = 16;
      }
    }
    
    // Animate to new position with appropriate zoom and bearing
    console.log("animating to new position with appropriate zoom and bearing");
    console.log("isValidHeading", isValidHeading);
    console.log("isMovingFastEnough", isMovingFastEnough);
    console.log("hasGoodAccuracy", hasGoodAccuracy);
    console.log("currentLocation", currentLocation);
    console.log("zoomLevel", zoomLevel);
    console.log("bearing", bearing);
    if (activeRoute) { // we only want to animate to the new position if we have an active route
      map.current.easeTo({
        center: [currentLocation.lng, currentLocation.lat],
        zoom: zoomLevel,
        bearing: bearing,
        duration: 500,
        pitch: 0
      });
    }
  }, [currentLocation, isMapInitialized, isValidLocation, activeRoute]);
  
  // Clean up route display when activeRoute changes to null
  useEffect(() => {
    if (!activeRoute && map.current && isMapInitialized) {
      // Hide route layers
      try {
        map.current.setLayoutProperty('route', 'visibility', 'none');
        map.current.setLayoutProperty('turns', 'visibility', 'none');
        map.current.setLayoutProperty('active-turn', 'visibility', 'none');
        map.current.setLayoutProperty('nearest-point', 'visibility', 'none');
      } catch (error) {
        console.error('Error clearing route display:', error);
      }
    }
  }, [activeRoute, isMapInitialized]);
  
  return (
    <div className="flex flex-1 relative">
      {!isMapInitialized && (
          <div className="flex flex-1 w-full h-[calc(100vh-80px)] z-10">
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-20">
              <div className="text-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-500 mx-auto mb-4"></div>
                <p className="text-lg font-medium text-gray-700">Kaart laden..</p>
              </div>
            </div>
          </div>
      )}
      <div 
        ref={mapContainer} 
        className="flex flex-1 w-full h-[calc(100vh-80px)] z-10"
      />
    </div>
  );
} 